import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Typography,
  Space,
  Alert,
  Form,
  Input,
  message,
  Divider,
  Steps,
  Row,
  Col,
  Spin,
  Empty,
  Modal
} from 'antd';
import {
  RobotOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  ArrowRightOutlined,
  ScanOutlined,
  WechatOutlined,
  CheckOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { qrcodeApi, statusApi, accountsApi, openclawApi } from '../../../services/jiyuMailboxApi';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

interface WechatConfigPanelProps {
  onConfigSuccess: (configId: string) => void;
  onSwitchToReminder: () => void;
  currentConfigId: string;
  isConfigured: boolean;
}

const WechatConfigPanel: React.FC<WechatConfigPanelProps> = ({
  onConfigSuccess,
  onSwitchToReminder,
  currentConfigId,
  isConfigured
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [qrCodeData, setQrCodeData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [serviceStatus, setServiceStatus] = useState<any>(null);
  const [connectedAccounts, setConnectedAccounts] = useState<any[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCheckingConfig, setIsCheckingConfig] = useState(false);
  const [previousAccountCount, setPreviousAccountCount] = useState(0);

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  // 检查服务状态
  const checkServiceStatus = async () => {
    try {
      setIsLoading(true);
      const status = await statusApi.getServiceStatus();
      setServiceStatus(status);
      console.log('服务状态:', status);
    } catch (error) {
      console.error('获取服务状态失败:', error);
      message.error('无法连接到OpenClaw服务，请检查服务是否启动');
    } finally {
      setIsLoading(false);
    }
  };

  // 获取已连接的账号
  const fetchConnectedAccounts = async () => {
    try {
      const accounts = await accountsApi.getAll();
      setConnectedAccounts(accounts);
      console.log('已连接账号:', accounts);
      return accounts;
    } catch (error) {
      console.error('获取账号列表失败:', error);
      return [];
    }
  };

  // 生成二维码
  const generateQRCode = async () => {
    try {
      setIsGenerating(true);

      // 记录当前账号数量
      const currentAccounts = await fetchConnectedAccounts();
      setPreviousAccountCount(currentAccounts.length);

      const result = await qrcodeApi.create({
        timeout: 300,
        add_text: true
      });

      console.log('二维码生成成功:', result);
      setQrCodeData(result);
      setCurrentStep(1);

      // 开始轮询二维码状态
      startStatusPolling(result.qr_id);
    } catch (error) {
      console.error('生成二维码失败:', error);
      message.error('生成二维码失败，请重试');
    } finally {
      setIsGenerating(false);
    }
  };

  // 开始状态轮询
  const startStatusPolling = (qrId: string) => {
    const interval = setInterval(async () => {
      try {
        const status = await qrcodeApi.getStatus(qrId);
        console.log('二维码状态:', status);

        if (status.status === 'confirmed' || status.status === 'connected') {
          clearInterval(interval);
          setCurrentStep(2);
          message.success('微信已成功连接！');
          onConfigSuccess(qrId);
          fetchConnectedAccounts();
        } else if (status.status === 'expired' || status.status === 'failed') {
          clearInterval(interval);
          message.error('二维码已失效，请重新生成');
          setCurrentStep(0);
        }
      } catch (error) {
        console.error('检查状态失败:', error);
      }
    }, 3000); // 每3秒检查一次

    setStatusCheckInterval(interval);
  };

  // 复制二维码URL
  const copyQRCodeUrl = () => {
    if (qrCodeData?.qr_url) {
      navigator.clipboard.writeText(qrCodeData.qr_url);
      message.success('二维码URL已复制');
    }
  };

  // 重新开始
  const handleReset = () => {
    setCurrentStep(0);
    setQrCodeData(null);
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
  };

  // 绑定 agent 并重启网关
  const bindAgentAndRestartGateway = async (accountId: string): Promise<boolean> => {
    try {
      message.loading({ content: '正在绑定 Agent...', key: 'bindAgent' });

      // 绑定 agent
      const bindResult = await openclawApi.bindAgent(accountId);
      if (!bindResult.success) {
        message.error({ content: `绑定 Agent 失败: ${bindResult.message}`, key: 'bindAgent' });
        return false;
      }
      message.success({ content: 'Agent 绑定成功！', key: 'bindAgent' });

      // 重启网关
      message.loading({ content: '正在重启网关...', key: 'restartGateway' });
      const restartResult = await openclawApi.restartGateway();
      if (!restartResult.success) {
        message.error({ content: `重启网关失败: ${restartResult.message}`, key: 'restartGateway' });
        return false;
      }
      message.success({ content: '网关重启成功！', key: 'restartGateway' });

      return true;
    } catch (error) {
      console.error('绑定 agent 或重启网关失败:', error);
      message.error('配置后处理失败，请检查控制台');
      return false;
    }
  };

  // 检查配置成功
  const handleCheckConfigSuccess = async () => {
    try {
      setIsCheckingConfig(true);

      // 获取当前已连接账号
      const accounts = await fetchConnectedAccounts();

      if (accounts.length > previousAccountCount) {
        // 有新连接
        const newAccount = accounts[0]; // 最新的账号

        message.loading({ content: '正在配置...', key: 'configuring' });

        // 绑定 agent 并重启网关
        const success = await bindAgentAndRestartGateway(newAccount.account_id);
        message.destroy('configuring');

        if (success) {
          // 保存到本地存储
          localStorage.setItem('wechat_account_id', newAccount.account_id);
          localStorage.setItem('wechat_user_id', newAccount.user_id || '');
          localStorage.setItem('wechat_configured', 'true');

          message.success('检测到新连接成功！');

          // 清除轮询
          if (statusCheckInterval) {
            clearInterval(statusCheckInterval);
            setStatusCheckInterval(null);
          }

          // 进入完成步骤
          setCurrentStep(2);
          setConnectedAccounts(accounts);
          onConfigSuccess(newAccount.account_id);
        }
      } else {
        // 没有新增通道，弹窗提示
        Modal.confirm({
          title: '未检测到新连接',
          content: '没有增加新的通道，好像是已经存在的账号',
          okText: '重新生成',
          cancelText: '下一步',
          onOk: () => {
            // 重新生成二维码
            generateQRCode();
          },
          onCancel: async () => {
            // 直接进入下一步（完成步骤）
            if (statusCheckInterval) {
              clearInterval(statusCheckInterval);
              setStatusCheckInterval(null);
            }
            // 如果有已连接账号，保存第一个账号的信息
            if (accounts.length > 0) {
              const account = accounts[0];

              message.loading({ content: '正在配置...', key: 'configuring' });

              // 绑定 agent 并重启网关
              const success = await bindAgentAndRestartGateway(account.account_id);
              message.destroy('configuring');

              if (success) {
                localStorage.setItem('wechat_account_id', account.account_id);
                localStorage.setItem('wechat_user_id', account.user_id || '');
                localStorage.setItem('wechat_configured', 'true');
                setConnectedAccounts(accounts);
                onConfigSuccess(account.account_id);
              }
            }
            setCurrentStep(2);
          },
        });
      }
    } catch (error) {
      console.error('检查配置失败:', error);
      message.error('检查配置失败，请重试');
    } finally {
      setIsCheckingConfig(false);
    }
  };

  // 检查服务
  const handleCheckService = async () => {
    await checkServiceStatus();
  };

  useEffect(() => {
    checkServiceStatus();
    if (isConfigured) {
      fetchConnectedAccounts();
    }
  }, [isConfigured]);

  return (
    <Card style={{
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
      border: '1px solid #e8f4ff'
    }}>
      <Title level={3} style={{ color: '#1890ff', marginBottom: '10px' }}>
        <RobotOutlined /> 微信机器人配置
      </Title>

      <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
        配置OpenClaw微信机器人，让小龙虾AI帮你发送暖心提醒
      </Paragraph>

      <Steps current={currentStep} style={{ marginBottom: '30px' }}>
        <Step title="开始配置" icon={<RobotOutlined />} />
        <Step title="扫描二维码" icon={<ScanOutlined />} />
        <Step title="完成配置" icon={<CheckOutlined />} />
      </Steps>

      {currentStep === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>
            <RobotOutlined style={{ color: '#1890ff' }} />
          </div>

          {serviceStatus?.status === 'healthy' ? (
            <Alert
              message="OpenClaw服务正常运行"
              description={`版本: ${serviceStatus.version} | 运行时间: ${Math.floor(serviceStatus.uptime / 60)} 分钟`}
              type="success"
              showIcon
              style={{ marginBottom: '30px', textAlign: 'left' }}
            />
          ) : (
            <Alert
              message="无法连接到OpenClaw服务"
              description="请确保OpenClaw服务已启动并运行在正确的地址"
              type="error"
              showIcon
              style={{ marginBottom: '30px', textAlign: 'left' }}
            />
          )}

          <Space direction="vertical" size="large">
            <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'left' }}>
              <Text strong style={{ fontSize: '16px', marginBottom: '12px', display: 'block' }}>
                配置步骤：
              </Text>
              <ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
                <li>点击下方按钮生成微信登录二维码</li>
                <li>使用微信扫描二维码登录</li>
                <li>登录成功后即可开始使用</li>
              </ol>
            </div>

            <Button
              type="primary"
              size="large"
              icon={<WechatOutlined style={{ fontSize: '18px' }} />}
              onClick={generateQRCode}
              loading={isGenerating}
              style={{
                height: '50px',
                fontSize: '16px',
                borderRadius: '8px'
              }}
            >
              生成微信登录二维码
            </Button>

            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={handleCheckService}
              loading={isLoading}
            >
              检查服务状态
            </Button>
          </Space>
        </div>
      )}

      {currentStep === 1 && qrCodeData && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Alert
            message="请使用微信扫描下方二维码"
            description="二维码有效期为5分钟，请及时扫描"
            type="info"
            showIcon
            style={{ marginBottom: '24px', textAlign: 'left' }}
          />

          <div style={{
            maxWidth: '400px',
            margin: '0 auto 30px',
            padding: '20px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
          }}>
            {qrCodeData.qr_image_url ? (
              <img
                src={qrCodeData.qr_image_url}
                alt="微信登录二维码"
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto'
                }}
              />
            ) : (
              <Empty description="二维码加载中..." />
            )}
          </div>

          <Space size="middle">
            <Button
              type="primary"
              size="large"
              icon={<CheckOutlined />}
              onClick={handleCheckConfigSuccess}
              loading={isCheckingConfig}
              style={{
                height: '50px',
                fontSize: '16px',
                borderRadius: '8px'
              }}
            >
              已配置成功
            </Button>
          </Space>
        </div>
      )}

      {currentStep === 2 && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
          </div>

          <Title level={4} style={{ color: '#52c41a', marginBottom: '16px' }}>
            微信配置成功！
          </Title>

          <Paragraph style={{ fontSize: '16px', marginBottom: '30px' }}>
            微信机器人已成功连接，现在可以开始配置提醒了
          </Paragraph>

          {connectedAccounts.length > 0 && (
            <div style={{
              maxWidth: '500px',
              margin: '0 auto 30px',
              padding: '20px',
              background: '#f0f9ff',
              borderRadius: '8px',
              textAlign: 'left'
            }}>
              <Text strong style={{ color: '#1890ff', marginBottom: '12px', display: 'block' }}>
                已连接账号 ({connectedAccounts.length})
              </Text>
              {connectedAccounts.map((account) => (
                <div
                  key={account.account_id}
                  style={{
                    padding: '12px',
                    background: 'white',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: account.status === 'connected' ? '#52c41a' : '#ff4d4f',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  >
                    {account.status === 'connected' ? '✓' : '✗'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      {account.user_id || '未知用户'}
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {account.account_id.slice(0, 12)}...
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Space size="middle">
            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              onClick={onSwitchToReminder}
            >
              配置提醒
            </Button>
            <Button
              type="default"
              onClick={handleReset}
            >
              重新配置
            </Button>
          </Space>
        </div>
      )}
    </Card>
  );
};

export default WechatConfigPanel;
